import * as React from 'react'
import { cn } from '../../lib/utils'

export interface DealKanbanCardProps extends React.HTMLAttributes<HTMLDivElement> {
	/** Channel the enquiry came from — a badge. */
	source?: React.ReactNode
	date?: string
	/** Message counter. Omitted entirely when the deal has no correspondence. */
	messages?: React.ReactNode
	/** The object, as a link into its card. */
	address: React.ReactNode
	/** Monthly rate, already formatted and grouped. */
	price?: string
	/** The client, as a link into their profile. */
	clientName?: React.ReactNode
	clientPhone?: string
	/** What happens next — a viewing, a call. Absent when nothing is booked. */
	nextEvent?: React.ReactNode
	/** Responsible manager: their avatar, no name — the column is narrow. */
	manager?: React.ReactNode
}

/**
 * A deal on the board.
 *
 * Three blocks, read top to bottom: where the enquiry came from, what it is
 * about, and who to talk to. The last line answers the question a manager
 * scanning the board actually has — what happens next, and whose it is.
 *
 * The object and the client are links rather than the whole card being one:
 * from a board, the two jumps worth a click are into the property and into the
 * client, and a card-wide handler makes both unreachable.
 */
const DealKanbanCard = React.forwardRef<HTMLDivElement, DealKanbanCardProps>(
	(
		{ className, source, date, messages, address, price, clientName, clientPhone, nextEvent, manager, ...props },
		ref
	) => (
		<div
			ref={ref}
			data-slot="deal-kanban-card"
			className={cn(
				'flex w-full flex-col overflow-clip rounded-(--radius-md) border border-(--color-border) bg-(--color-popover)',
				'cursor-pointer transition-shadow hover:shadow-sm',
				className
			)}
			{...props}
		>
			<div className="flex flex-col gap-2 p-3">
				<div className="flex items-center gap-2">
					<div className="min-w-0 shrink">{source}</div>
					<span className="ml-auto shrink-0 text-xs leading-5 text-(--color-muted-foreground)">
						{date}
					</span>
					{messages && <div className="shrink-0">{messages}</div>}
				</div>

				<div className="flex flex-col gap-1 leading-5">
					<div className="line-clamp-2 text-sm font-[600] text-(--color-popover-foreground)">
						{address}
					</div>
					{price && (
						<span className="text-sm font-[600] text-(--color-info)">{price}</span>
					)}
				</div>
			</div>

			{(clientName || clientPhone || nextEvent || manager) && (
				<div className="flex flex-col gap-2 border-t border-(--color-border) p-3">
					{clientName && (
						<div className="truncate text-sm text-(--color-foreground)">{clientName}</div>
					)}
					{clientPhone && (
						<span className="truncate text-sm text-(--color-muted-foreground)">
							{clientPhone}
						</span>
					)}
					{(nextEvent || manager) && (
						<div className="flex min-h-6 items-center justify-between gap-2">
							<div className="min-w-0 shrink">{nextEvent}</div>
							<div className="shrink-0">{manager}</div>
						</div>
					)}
				</div>
			)}
		</div>
	)
)

DealKanbanCard.displayName = 'DealKanbanCard'
export { DealKanbanCard }
